/**
 * 🛡️ Subscription Guard
 *
 * يحوّل provider users الـ expired إلى /subscription.
 *
 * Behavior:
 *   - admin/vendor (legacy): ما يتأثر (يكمل عادي)
 *   - provider user مع subscription active: يكمل
 *   - provider user مع subscription expired/suspended: redirect لـ /subscription
 *
 * يستخدم في MainLayout أو router level.
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

import { subscriptionService } from '@application/services/SubscriptionService';
import type { ProviderSubscription } from '@application/services/SubscriptionService';
import { useUser } from '@application/stores/auth.store';

const ALLOWED_PATHS_WHEN_EXPIRED = [
  '/subscription',
  '/change-password',
  '/logout',
];

export function SubscriptionGuard() {
  const user = useUser();
  const location = useLocation();
  const [subscription, setSubscription] = useState<ProviderSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  // Provider user → جلب subscription
  useEffect(() => {
    if (!user?.providerId) {
      setLoading(false);
      setSubscription(null);
      return;
    }

    const unsub = subscriptionService.subscribeToProviderSubscription(
      user.providerId,
      (sub) => {
        setSubscription(sub);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [user?.providerId]);

  // غير provider user → يكمل عادي
  if (!user?.providerId) return <Outlet />;

  // أثناء التحميل
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-royal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white text-center"
        >
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p className="text-sm opacity-80">جاري التحقق من الاشتراك...</p>
        </motion.div>
      </div>
    );
  }

  // Subscription active → يكمل عادي
  if (subscription?.isActive) return <Outlet />;

  // Expired → فقط /subscription (و paths مسموحة) متاحة
  const isAllowedPath = ALLOWED_PATHS_WHEN_EXPIRED.some((p) =>
    location.pathname.startsWith(p)
  );

  if (isAllowedPath) return <Outlet />;

  // Redirect لـ subscription
  return <Navigate to="/subscription" replace />;
}
